import React, { useState, useMemo, useContext, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Progress,
  Spacer,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import {
  CrossIcon,
  RedCrossIcon,
  TickIcon,
  UploadIcon,
} from '../../../constants/IconData';
import {
  acceptStyle,
  baseStyle,
  focusedStyle,
  rejectStyle,
} from '../../chart_upload/styles';
import { UploadContext } from '../../../providers/ChartUploadProvider';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import AppColors from '../../../constants/AppColors';

const MAX_FILES = 20;
const SUPPORTED_FORMATS = ['XLS', 'XLSX', 'PDF', 'DOC', 'DOCX', 'HL7'];

const formatFileSize = size => {
  if (!size && size !== 0) return '0 KB';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const AddAdditionalDocsModal = ({
  isOpen,
  onClose,
  auditUpload,
  onUploadComplete,
}) => {
  const chartUploadContext = useContext(UploadContext);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setFileError('');
    }
  }, [isOpen]);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'text/plain': ['.hl7'],
      },
      maxFiles: MAX_FILES,
      useFsAccessApi: false,
      onDrop: acceptedFiles => {
        setFileError('');
        setFiles(prevFiles => {
          const existingKeys = new Set(
            prevFiles.map(f => `${f.name}-${f.file.size}-${f.file.lastModified}`)
          );
          const newFiles = acceptedFiles
            .filter(f => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`))
            .map(f => ({
              file: f,
              name: f.name,
              size: formatFileSize(f.size),
              value: 0,
              uploaded: false,
              cancelled: false,
            }));

          const nextFiles = [...prevFiles, ...newFiles];
          if (nextFiles.length > MAX_FILES) {
            setFileError(`You can attach up to ${MAX_FILES} files at a time.`);
            return nextFiles.slice(0, MAX_FILES);
          }

          if (!newFiles.length && acceptedFiles.length) {
            setFileError('Those files are already in the upload queue.');
          }

          return nextFiles;
        });
      },
      onDropRejected: () => {
        setFileError('Please upload supported files only: XLS, XLSX, PDF, DOC, DOCX, or HL7.');
      },
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      minHeight: 160,
      justifyContent: 'center',
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: isDragReject ? '#F9480F' : isDragAccept ? '#43DAD4' : '#D7E3F8',
      borderRadius: 8,
      background: isDragAccept ? '#F0FFFE' : '#FBFCFF',
      color: '#1F2A44',
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleSubmit = async () => {
    if (files.length > 0) {
      const chartId = auditUpload.id;
      const filesToUpload = files.map(f => f.file);
      await chartUploadContext.attachFiles(filesToUpload, chartId, updatedFiles => {
        setFiles(prevFiles =>
          prevFiles.map((file, index) => ({
            ...file,
            ...(updatedFiles[index] || {}),
          }))
        );
      });
      onUploadComplete();
    }
  };

  const handleRemoveFile = index => {
    setFileError('');
    setFiles(files.filter((_, i) => i !== index));
  };

  const hasUploadInProgress = files.some(f => f.value > 0 && f.value < 100);
  const completedCount = files.filter(f => f.uploaded).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="8px" overflow="hidden">
        <ModalHeader px={6} py={5} borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={3} align="center">
            <Box>
              <Text fontSize="18px" fontWeight={700} color={AppColors.secondary}>
                Additional Documents
              </Text>
              <Text fontSize="13px" fontWeight={400} color="gray.500">
                Chart ID: {auditUpload?.upload_id || 'N/A'}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top="18px" />
        <ModalBody px={6} py={5}>
          <VStack spacing={4} align="stretch">
            <Box
              {...getRootProps({ style })}
              p={6}
              width="100%"
              textAlign="center"
              cursor="pointer"
            >
              <input {...getInputProps()} />
              <UploadIcon disableTooltip style={{ width: 34, height: 34, margin: '0 auto' }} />
              <Text mt={3} fontWeight={700} color={AppColors.secondary}>
                Drop files here or browse from your computer
              </Text>
              <Text mt={1} fontSize="13px" color="gray.500">
                Supported: {SUPPORTED_FORMATS.join(', ')}
              </Text>
            </Box>

            {fileError && (
              <Box bg="red.50" border="1px solid" borderColor="red.100" borderRadius="6px" px={3} py={2}>
                <Text color="red.600" fontSize="13px">
                  {fileError}
                </Text>
              </Box>
            )}

            <Box>
              <HStack mb={2}>
                <Text fontWeight={700} color="gray.700">
                  Upload queue
                </Text>
                <Spacer />
                {completedCount > 0 && (
                  <Text fontSize="12px" color="gray.500">
                    {completedCount} uploaded
                  </Text>
                )}
              </HStack>

              {files.length > 0 ? (
                <PerfectScrollbar style={{ maxHeight: '210px', paddingRight: '6px' }}>
                  <VStack spacing={2} align="stretch">
                    {files.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        p={3}
                        border="1px solid"
                        borderColor={file.cancelled ? 'red.100' : 'gray.100'}
                        borderRadius="6px"
                        bg={file.cancelled ? 'red.50' : 'white'}
                      >
                        <HStack spacing={3}>
                          <Box minW={0}>
                            <Text fontSize="14px" fontWeight={600} color="gray.700" isTruncated>
                              {file.name}
                            </Text>
                            <Text fontSize="12px" color="gray.500">
                              {file.size}
                            </Text>
                          </Box>
                          <Spacer />
                          {file.uploaded && <TickIcon style={{ width: 22, height: 22 }} />}
                          {file.cancelled && <RedCrossIcon style={{ width: 22, height: 22 }} />}
                          {!file.uploaded && !file.cancelled && !chartUploadContext.isSubmitting && (
                            <CrossIcon
                              onClick={() => handleRemoveFile(index)}
                              style={{ cursor: 'pointer', width: 18, height: 18 }}
                            />
                          )}
                        </HStack>
                        {(file.value > 0 || file.uploaded || file.cancelled) && (
                          <Progress
                            mt={3}
                            value={file.value}
                            size="xs"
                            colorScheme={file.cancelled ? 'red' : 'teal'}
                            borderRadius={10}
                          />
                        )}
                      </Box>
                    ))}
                  </VStack>
                </PerfectScrollbar>
              ) : (
                <Box border="1px solid" borderColor="gray.100" borderRadius="6px" py={5} textAlign="center">
                  <Text color="gray.500" fontSize="13px">
                    Selected files will appear here before upload.
                  </Text>
                </Box>
              )}
              <Text mt={2} fontSize="12px" color="gray.500">
                Up to {MAX_FILES} files can be uploaded at once.
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter px={6} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" onClick={onClose} isDisabled={chartUploadContext.isSubmitting}>
            Cancel
          </Button>
          <Button
            bg={AppColors.secondary}
            color="white"
            ml={3}
            onClick={handleSubmit}
            isLoading={chartUploadContext.isSubmitting}
            isDisabled={files.length === 0 || hasUploadInProgress}
            _hover={{ bg: AppColors.primaryColorHover || '#002968' }}
          >
            Upload Documents
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAdditionalDocsModal;
